import { FamilyMember, CreateFamilyMemberInput, UpdateFamilyMemberInput } from '@/types/family-member';
import { LocalFamilyMembersStore } from '@/lib/local/LocalFamilyMembersStore';
import { LocalCalendarMemberMappingStore } from '@/lib/local/LocalCalendarMemberMappingStore';
import { FamilyMemberValidator } from './FamilyMemberValidator';
import { FamilyMemberColorPalette } from './FamilyMemberColorPalette';
import { FamilyMemberEvents } from './FamilyMemberEvents';

/**
 * Business logic manager for family members
 * Handles CRUD operations with validation and color management
 */
export class FamilyMemberManager {
  private readonly familyMembersStore: LocalFamilyMembersStore;
  private readonly calendarMemberMappingStore: LocalCalendarMemberMappingStore;

  /**
   * Create a new FamilyMemberManager instance
   * @param familyMembersStore - Scoped family members store
   * @param calendarMemberMappingStore - Scoped calendar member mapping store
   */
  constructor(
    familyMembersStore: LocalFamilyMembersStore,
    calendarMemberMappingStore: LocalCalendarMemberMappingStore,
  ) {
    this.familyMembersStore = familyMembersStore;
    this.calendarMemberMappingStore = calendarMemberMappingStore;
  }

  /**
   * Get all family members
   */
  async getAll(): Promise<FamilyMember[]> {
    return this.familyMembersStore.getAll();
  }

  /**
   * Get a family member by ID
   */
  async getById(id: string): Promise<FamilyMember | null> {
    return this.familyMembersStore.getById(id);
  }

  /**
   * Get the "Me" family member
   */
  async getMe(): Promise<FamilyMember | null> {
    return this.familyMembersStore.getMe();
  }

  /**
   * Get the next available color from the palette
   */
  async getNextAvailableColor(): Promise<string> {
    const members = await this.familyMembersStore.getAll();
    const usedColors = members.map(m => m.color).filter(Boolean);
    const nextColor = FamilyMemberColorPalette.getNextAvailableColor(usedColors);

    // If all colors are used, return the first color (will allow duplicate)
    return nextColor?.hex || FamilyMemberColorPalette.getAllColors()[0].hex;
  }

  /**
   * Create a new family member with validation
   */
  async create(input: CreateFamilyMemberInput): Promise<FamilyMember> {
    // Validate input (accepts any valid hex color)
    const validation = FamilyMemberValidator.validateCreateInput(input);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Check for color uniqueness (warn but allow)
    const colorInUse = await this.familyMembersStore.isColorInUse(input.color);
    if (colorInUse) {
      console.warn(`Color ${input.color} is already in use by another family member`);
    }

    const member = await this.familyMembersStore.create(input);
    FamilyMemberEvents.emitMembersChanged();
    return member;
  }

  /**
   * Update a family member with validation
   */
  async update(id: string, updates: UpdateFamilyMemberInput): Promise<FamilyMember | null> {
    // Validate input (accepts any valid hex color)
    const validation = FamilyMemberValidator.validateUpdateInput(updates);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Check for color uniqueness if color is being updated
    if (updates.color) {
      const colorInUse = await this.familyMembersStore.isColorInUse(updates.color, id);
      if (colorInUse) {
        console.warn(`Color ${updates.color} is already in use by another family member`);
      }
    }

    const member = await this.familyMembersStore.update(id, updates);
    if (member) {
      FamilyMemberEvents.emitMembersChanged();
    }
    return member;
  }

  /**
   * Delete a family member
   * Also removes from all calendar mappings
   */
  async delete(id: string): Promise<boolean> {
    // Get member to check if it's "Me"
    const member = await this.familyMembersStore.getById(id);
    if (!member) return false;

    if (member.isMe) {
      throw new Error('Cannot delete the "Me" family member');
    }

    // Remove from all calendar mappings first
    await this.calendarMemberMappingStore.removeMemberFromAllMappings(id);

    const deleted = await this.familyMembersStore.delete(id);
    if (deleted) {
      FamilyMemberEvents.emitMembersChanged();
    }
    return deleted;
  }

  /**
   * Get multiple family members by IDs
   */
  async getByIds(ids: string[]): Promise<FamilyMember[]> {
    return this.familyMembersStore.getByIds(ids);
  }

  /**
   * Get all colors currently in use
   */
  async getUsedColors(): Promise<string[]> {
    const members = await this.familyMembersStore.getAll();
    return members.map(m => m.color).filter(Boolean);
  }

  /**
   * Check if a name is already in use
   */
  async isNameInUse(name: string, excludeId?: string): Promise<boolean> {
    const members = await this.familyMembersStore.getAll();
    const normalizedName = name.trim().toLowerCase();
    return members.some(
      m => m.name.toLowerCase() === normalizedName && m.id !== excludeId
    );
  }
}
